import { PLAN_TERM, SUBSCRIPTION_PLANS } from '@/constants/subscription';
import { ICTAOption } from './page-cta';

export interface ITrainData {
  blob_url: string;
  original_file_name: string;
  upload_timestamp: string;
  updated_date: string;
  processed: boolean;
  file_id: number;
}

export interface IFile {
  name: string;
  url: string;
}

export interface IDashboardScore {
  total_conversations: number;
  avg_messages: number;
  positive_feedback: number;
  negative_feedback: number;
  no_feedback: number;
  positive_feedback_percentage: number;
  negative_feedback_percentage: number;
  campaigns?: {
    active: number;
    success_rate: number;
    top_source: string;
    total: number;
  };
  userEngagementSummary?: {
    counts?: {
      high: number;
      medium: number;
      low: number;
    };
    highEngagementUsers: number;
    mediumEngagementUsers: number;
    lowEngagementUsers: number;
    totalUsers: number;
    thresholds: {
      low: number;
      high: number;
    };
  };
  trends?: {
    engagement: {
      data: any[];
    };
    visitors: {
      data: any[];
      trend: {
        percentage: number;
        direction: string;
        current_period: number;
        previous_period: number;
      };
      visits_graph?: { date: string; total_visits: number }[];
    };
  };
}

export interface IChatQueryBody {
  threadId: string;
  query: string;
}

export interface IThread {
  threadId: string;
  origin_url?: string;
  title: string;
  status: string;
  date: string;
  userEmail?: string;
  isInitialThread?: boolean;
  isLocalThread?: boolean;
  interactions?: IThreadInteraction[];
  companyInfo?: {
    name: string;
    domain: string;
    industry: string | null;
    employeeRange: string;
    revenue: {
      year: string | null;
      amount: string | null;
    };
    foundedYear: string | null;
    description: string | null;
    phone: string | null;
    logo_url: string;
    socialLinks: {
      linkedin: string;
      facebook: string;
      twitter: string;
      crunchbase: string;
    };
  };
}

export interface IThreadInteraction {
  ai: string;
  id: number;
  user: string;
  keywords: string;
  recommendations?: IChatQueryRecommendation;
  aiLogo?: string;
  is_email?: boolean;
  feedback?: boolean;
  event?: string;
  timestamp?: string;
}

export interface Persona {
  persona_id: number;
  name: string;
  description: string;
  is_common: boolean;
  selected: boolean;
}

export interface IRecommendationInfo {
  id?: number;
  product_id?: number;
  product_image?: string;
  product_name?: string;
  name?: string;
  description?: string;
  product_description?: string;
  img?: string;
  vendorname: string;
  logo?: string;
  slug?: string;
  recommendationType?: string;
  vendorid: number;
  device_type?: string;
  type: string;
  vendorlogo: string;
  vendorslug: string;
  category: string;
  verified: boolean;
}

export interface IChatQueryRecommendation {
  [x: string]: IRecommendationInfo[];
}

export interface IRequestQuoteForm {
  quantity: number;
  notes?: string;
  productId?: number;
  vendorId?: number;
  type?: string;
  requestedByDate: Date;
}

export enum RECOMMENDATION_TYPE {
  DEVICES = 'devices',
  CONNECTIVITY = 'connectivity',
  PLATFORM = 'platform',
  HARDWARE = 'hardware',
  SERVICES = 'services',
}

export interface IProduct {
  id: number;
  slug: string;
  vendorid: number;
  company: string;
  name: string;
  img: string;
  product_type: string | string[];
  device_serp_ai: {
    features: string[];
    introduction: string;
  };
  website: string;
  device_type: string;
  network_technology: string;
  industries: string;
  lte: string;
  vendorName: string;
  vendorLogo: string;
  type: string;
  product_image: string;
  vendorname: string;
  vendorlogo: string;
  product_url?: string;
  product_description: string;
  product_details: {
    name: string;
    description: string;
  }[];
  usecase: string[];
  product_name: string;
  verified: boolean;
}

export interface ISpecification {
  name: string;
  value: string;
  icon: string;
}

export interface IRecommendationProductDetail extends IProduct {
  Product: IProduct[];
  specifications: ISpecification[];
  alternate_products: IRecommendationInfo[];
}

export interface IProjectItem {
  project_id: number;
  name: string;
  created_at: string;
  user_id: string;
}

export interface IAddProductToProjectBody {
  type: string;
  productId: number | string;
}
export interface ICreateNewProjectBody {
  userId: string;
  name: string;
}

export type CheckoutSessionType = 'vendor_onboarding' | 'access_bot';
export interface ICreateCheckoutSessionBody {
  type?: CheckoutSessionType;
  env: string;
  stripePriceId?: string; // NEW - primary identifier for checkout
  planName?: string; // Make optional (for context/logging)
  term?: PLAN_TERM; // Make optional (for context/logging)
  amount?: number; // DEPRECATED - will be removed
  credits?: number; // DEPRECATED - will be removed
}

export interface IRequestQuoteForm {
  quantity: number;
  notes?: string;
  productId?: number;
  vendorId?: number;
  type?: string;
  requestedByDate: Date;
}

export interface IQuoteVerificationForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  website?: string;
}

export interface ICreateQuotesBody {
  item: IRequestQuoteForm;
}

export interface ICreateQuotesSuccess {
  message: string;
  quoteId: number;
  subscriptionPlan: SUBSCRIPTION_PLANS;
}

export interface IGreetingsFeedback {
  threadId?: string;
  interaction?: number;
  feedback: boolean;
  source: string;
}
export interface ISavedProduct {
  project_id: number;
  products: IRecommendationInfo[];
}

export interface IInternalAgentInteraction {
  id: number;
  timestamp: string; // ISO 8601 format
  AI: string;
  bot_logo: string;
  initials: string;
  user: string;
}

export interface InternalAgentSessionDetail {
  session_id: string;
  employee_name: string;
  created_date: string; // ISO 8601 format
  status: string; // e.g., "active", "inactive"
  interactions: IInternalAgentInteraction[];
}

export interface IInternalAgentSummary {
  conversationCount: number;
  topUsers: {
    employee_name: string;
    session_count: number;
  };
  sessionDetails: InternalAgentSessionDetail[];
}

export interface ILinksForCTAResponse {
  currentPage: number;
  data: ILinkForCTA[];
  totalItems: number;
  totalPages: number;
}

export interface ILinkForCTA {
  link_id: number;
  link: string;
  dateAdded?: string;
  options: {
    option_a: ICTAOption;
    option_b: ICTAOption;
    option_c: ICTAOption;
  };
}

export interface ICreateEntryBody {
  chat_token: string;
  training_data: {
    trigger_phrase: string;
    content_type: string;
    content_value: string;
    is_active: boolean;
  }[];
}

export interface IEntryDataResponse {
  data: {
    chat_token: string;
    content_type: string;
    content_value: string;
    created_at: string;
    created_by: string;
    id: number;
    is_active: boolean;
    priority: number;
    trigger_phrase: string;
    updated_at: string;
    vendorid: number;
  }[];
}
